import { useCallback, useState } from 'react';
import { useTranslation } from 'src/hooks/use-translation';
import { useQueryClient } from '@tanstack/react-query';

import { acceptInvitation as acceptInvitationApi } from 'src/api';
import { useAuthContext } from 'src/auth/hooks';
import { useBoolean } from 'src/hooks/use-boolean';
import { useSnackbar } from 'src/components/snackbar';
import { ErrorResponse, INVITATION_ERROR_CODES, InvitationDetails } from 'src/types';
import { platformKeys } from 'src/utils/query-keys';
import { runWithCallbacks } from 'src/utils/run-with-callbacks';

import { InviteError } from '../types';

const ERROR_CODE_TO_STATE: Record<INVITATION_ERROR_CODES, InviteError> = {
  [INVITATION_ERROR_CODES.EXPIRED]: 'expired',
  [INVITATION_ERROR_CODES.ALREADY_ACCEPTED]: 'already-accepted',
  [INVITATION_ERROR_CODES.WRONG_ACCOUNT]: 'wrong-account',
};

export const useAcceptInvitation = (invitation: InvitationDetails | null) => {
  const { t } = useTranslation('dashboard/invitation-accept');
  const { changeActivePlatform } = useAuthContext();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { value: isAccepting, onTrue: onAcceptingStart, onFalse: onAcceptingEnd } = useBoolean();
  const [confirmError, setConfirmError] = useState<InviteError | null>(null);

  const accept = useCallback(
    async (onAccepted: () => void) => {
      if (!invitation) return;

      onAcceptingStart();
      setConfirmError(null);

      const onSuccess = () => {
        onAccepted();
        showSnackbar({
          message: t('accepted', { platform: invitation.platform.name }),
          severity: 'success',
        });
        queryClient.invalidateQueries({ queryKey: platformKeys.list() });
        changeActivePlatform(invitation.platform.id);
      };

      const onError = (err: ErrorResponse) => {
        console.error(err.message);
        setConfirmError(ERROR_CODE_TO_STATE[err.code as INVITATION_ERROR_CODES] ?? 'generic');
      };

      await runWithCallbacks(
        () => acceptInvitationApi({ invitation_id: invitation.id }),
        onSuccess,
        onError
      );
      onAcceptingEnd();
    },
    [
      invitation,
      changeActivePlatform,
      queryClient,
      showSnackbar,
      t,
      onAcceptingStart,
      onAcceptingEnd,
    ]
  );

  return { accept, isAccepting, confirmError };
};
